/* NetworkProfileTour — iter473 · one-time onboarding overlay
 * ─────────────────────────────────────────────────────────────────
 * iter862 · optional `step.image`/`step.imageAlt` renders a small
 * reference thumbnail in the popover, above the message text — used
 * by the welcome walkthrough's FLYT ID step to show a real example.
 * Absent on every other tour (Network tour, old app tour), so this is
 * purely additive.
 * A guided 8-step tooltip tour that fires the FIRST TIME an approved
 * athlete lands on their own /network/{slug} page. Triggers converge
 * from three entry paths (anonymous claim approved · Premium Push to
 * Network approved · Founding Athlete Push to Network approved) but
 * this component only cares about the surface — whoever mounted us
 * has already checked `isOwnerRaw && !user.has_seen_network_tour`.
 *
 * Design:
 *   • Dark backdrop covers the viewport with 55% opacity.
 *   • An SVG rect "hole" spotlights the target element with a soft
 *     blue ring so the user's eye lands on the right control.
 *   • Popover renders below (or above, when we'd overflow) with the
 *     short one-sentence instruction, step count, and Next / Skip /
 *     Skip All controls (Skip All is always visible, per spec).
 *   • Auto-scrolls target into view before measuring so long profiles
 *     don't spotlight offscreen coordinates.
 *   • Resilient: if a target selector isn't in the DOM yet we retry
 *     up to 20× at 100ms intervals, then quietly skip that step.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const PAD = 10;                          // pixels of breathing room around the spotlight
const POP_W = 320;                       // popover width — big enough for two lines, fits iPhone SE
const POP_GAP = 14;                      // gap between spotlight and popover
const RETRY_MS = 100;
const MAX_RETRIES = 20;

const NetworkProfileTour = ({ steps = [], open, onComplete, onSkipAll }) => {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [vp, setVp] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 390,
    h: typeof window !== 'undefined' ? window.innerHeight : 844,
  }));
  const retryRef = useRef(0);
  const rafRef = useRef(0);
  const step = steps[idx];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) {
      if (retryRef.current < MAX_RETRIES) {
        retryRef.current += 1;
        rafRef.current = setTimeout(measure, RETRY_MS);
      } else {
        // Target missing — advance to next step so the tour never hangs.
        setRect(null);
      }
      return;
    }
    retryRef.current = 0;
    // Scroll target into view (centered) before measuring so the hole
    // lands on a visible surface, not offscreen coordinates.
    try {
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    } catch (_) { /* older WebKit */ }
    // Give the smooth scroll one frame to settle.
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({
        x: Math.max(0, r.left - PAD),
        y: Math.max(0, r.top - PAD),
        w: Math.min(window.innerWidth, r.width + PAD * 2),
        h: Math.min(window.innerHeight, r.height + PAD * 2),
      });
    }, 260);
  }, [step]);

  // Reset + remeasure whenever step changes. Run `beforeEnter` (e.g.
  // switch the active tab) so the target renders BEFORE we look for it.
  useLayoutEffect(() => {
    if (!open || !step) return;
    retryRef.current = 0;
    setRect(null);
    try { step.beforeEnter?.(); } catch (_) { /* noop */ }
    // Let React commit the beforeEnter mutation before we scan the DOM.
    const t = setTimeout(measure, 60);
    return () => {
      clearTimeout(t);
      if (rafRef.current) clearTimeout(rafRef.current);
    };
  }, [open, idx, step, measure]);

  // Track viewport resize / orientation change so the spotlight stays
  // aligned when the user rotates their phone mid-tour.
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      setVp({ w: window.innerWidth, h: window.innerHeight });
      measure();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [open, measure]);

  const next = () => {
    if (idx < steps.length - 1) {
      setIdx(idx + 1);
    } else {
      onComplete?.();
    }
  };

  const skip = () => {
    // "Skip" advances to the next step (per spec — Skip All ends the tour).
    if (idx < steps.length - 1) {
      setIdx(idx + 1);
    } else {
      onComplete?.();
    }
  };

  const skipAll = () => {
    onSkipAll?.();
  };

  if (!open || !step) return null;

  // Popover placement — prefer below the spotlight; flip above when
  // the spotlight sits in the bottom third of the viewport.
  let popTop;
  let popLeft;
  // iter883 · popover height varies a lot by step — an image step (the
  // "Build your FLYT ID" reference thumbnail) is ~320px vs ~180px for a
  // plain text step. Estimating it per-step (instead of a flat 180)
  // fixes step 5/6 rendering off the bottom of the screen with its
  // Next button unreachable.
  const popH = step.image ? 320 : 180;
  if (rect) {
    const spaceBelow = vp.h - (rect.y + rect.h);
    if (spaceBelow >= popH + POP_GAP + 24) {
      popTop = rect.y + rect.h + POP_GAP;
    } else {
      popTop = Math.max(24, rect.y - popH - POP_GAP);
    }
    popLeft = Math.max(16, Math.min(vp.w - POP_W - 16, rect.x + rect.w / 2 - POP_W / 2));
  } else {
    // No target found — center popover.
    popTop = vp.h / 2 - 90;
    popLeft = Math.max(16, vp.w / 2 - POP_W / 2);
  }
  // Hard clamp — never let the card (and its Next button) run off the
  // bottom or top edge, whatever the placement math above chose.
  popTop = Math.max(16, Math.min(popTop, vp.h - popH - 16));

  const total = steps.length;
  const isLast = idx === total - 1;

  return (
    <div
      className="fixed inset-0 z-[9999]"
      data-testid="network-tour-overlay"
      aria-label="Network profile tour"
      role="dialog"
    >
      {/* Spotlight mask — SVG rect punches a hole in the dim layer so
          the highlighted element stays visible + interactive-looking.
          pointer-events-auto only on the mask so taps outside the
          spotlight don't leak to the page beneath. */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        aria-hidden="true"
        width={vp.w}
        height={vp.h}
      >
        <defs>
          <mask id="network-tour-mask">
            <rect x="0" y="0" width={vp.w} height={vp.h} fill="white" />
            {rect && (
              <rect
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                rx="16"
                ry="16"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width={vp.w}
          height={vp.h}
          fill="rgba(0,0,0,0.62)"
          mask="url(#network-tour-mask)"
        />
        {rect && (
          <rect
            x={rect.x}
            y={rect.y}
            width={rect.w}
            height={rect.h}
            rx="16"
            ry="16"
            fill="none"
            stroke="rgba(59,130,246,0.95)"
            strokeWidth="2"
            style={{ filter: 'drop-shadow(0 0 10px rgba(59,130,246,0.55))' }}
          />
        )}
      </svg>

      {/* Popover card */}
      <div
        className="absolute rounded-2xl bg-neutral-950 ring-1 ring-white/15 shadow-2xl p-4 pointer-events-auto overflow-y-auto"
        style={{ top: popTop, left: popLeft, width: POP_W, maxHeight: vp.h - 32 }}
        data-testid="network-tour-popover"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-widest text-blue-300 font-black">
            Step {idx + 1} of {total}
          </span>
          <button
            type="button"
            onClick={skipAll}
            className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white font-bold"
            data-testid="network-tour-skip-all"
          >
            Skip All
          </button>
        </div>
        {step.title && (
          <p className="font-display uppercase text-white text-base leading-tight mb-1" data-testid="network-tour-title">
            {step.title}
          </p>
        )}
        {step.image && (
          <img
            src={step.image}
            alt={step.imageAlt || ''}
            className="w-full h-28 object-cover rounded-xl mb-2 ring-1 ring-white/15"
            data-testid="network-tour-image"
          />
        )}
        <p className="text-sm text-white/85 leading-snug" data-testid="network-tour-message">
          {step.message}
        </p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={skip}
            className="text-xs uppercase tracking-widest text-white/60 hover:text-white font-bold"
            data-testid="network-tour-skip"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={next}
            className="px-4 py-2 rounded-full bg-white text-black text-xs uppercase tracking-widest font-black active:scale-[0.98] transition"
            data-testid="network-tour-next"
          >
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NetworkProfileTour;
